import { Trash2 } from "lucide-react";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";
import { TextArea } from "@/components/field";

import type { EditorMark } from "./marksHistory";

const MARK_LABELS: Record<EditorMark["type"], string> = {
  box: "Boxed area",
  ellipse: "Circled area",
  pencil: "Freehand",
  text: "Pin",
};

type MarksListProps = {
  isDisabled: boolean;
  marks: EditorMark[];
  onChangeInstruction: (n: number, instruction: string) => void;
  onRemove: (n: number) => void;
};

export function MarksList({
  isDisabled,
  marks,
  onChangeInstruction,
  onRemove,
}: MarksListProps) {
  return (
    <section className="flex flex-col gap-1" data-testid="marks-list">
      <h2 className="text-lg font-extrabold text-text-primary">
        What should change?
      </h2>
      <p className="text-sm text-text-tertiary">
        {marks.length === 0
          ? "Mark the ad with a box, a pencil, a pin or an ellipse, then say what you want done there."
          : "Each marker below matches a spot on the ad. Say what you want done there."}
      </p>
      <ul className="mt-3 flex flex-col gap-3">
        {marks.map((mark) => (
          <li
            className="group flex items-start gap-3"
            data-testid="mark-card"
            key={mark.n}
          >
            <span className="mt-2 flex size-7 shrink-0 items-center justify-center rounded-full bg-button-primary-bg text-sm font-extrabold text-button-primary-fg">
              {mark.n}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-bold text-text-tertiary">
                  {MARK_LABELS[mark.type]}
                </span>
                <Button
                  className="size-6 p-0 opacity-0 focus-visible:opacity-100 group-focus-within:opacity-100 group-hover:opacity-100"
                  isDisabled={isDisabled}
                  isIconOnly
                  onPress={() => onRemove(mark.n)}
                  size="sm"
                  variant="tertiary"
                >
                  <AccessibleIcon aria-label={`Delete mark ${mark.n}`}>
                    <Trash2 size={14} />
                  </AccessibleIcon>
                </Button>
              </div>
              <TextArea
                aria-label={`Instruction for mark ${mark.n}`}
                autoFocus={mark.n === marks.length}
                className="min-h-16 resize-none text-[15px] leading-[21px]"
                disabled={isDisabled}
                onChange={(event) =>
                  onChangeInstruction(mark.n, event.target.value)
                }
                placeholder="What should change here?"
                rows={2}
                value={mark.instruction}
              />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
